import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Card,
	Heading,
	Paragraph,
	QuillText,
	Separator,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

const Chevron = styled(Box)<{ rotate?: string }>`
	width: 24px;

	svg {
		display: block;

		transition: 200ms transform ease-out;
		${(props) => props.rotate && `transform: rotate(${props.rotate});`}

		path {
			fill: ${tokens.text.secondary};
		}
	}
`;

export default Skeleton(
	function JobCard(props: { job: graphql.JobPosition; userID: string }) {
		const { localize } = useLocalize();
		const { t } = useTranslation();

		const [isOpen, setIsOpen] = useState(false);
		const hasDescription = props.job.jobTitle.description;
		const jobLevel = props.job.jobLevel;

		return (
			<Card
				mb={3}
				onClick={
					hasDescription
						? () => {
								setIsOpen(!isOpen);
							}
						: undefined
				}
			>
				<Flex alignItems="center">
					<Box py={2} flexGrow={1} flexShrink={1}>
						<Heading>{localize(props.job.jobTitle.title).text}</Heading>
						{jobLevel && <Paragraph>{localize(jobLevel.title).text}</Paragraph>}
						<Flex>
							<Paragraph>
								{t('jobs.card.startDate', { date: moment(props.job.startDate).format('LL') })}
							</Paragraph>
							{props.job.endDate && (
								<>
									<Paragraph>&nbsp;·&nbsp;</Paragraph>
									<Paragraph>
										{t('jobs.card.endDate', { date: moment(props.job.endDate).format('LL') })}
									</Paragraph>
								</>
							)}
						</Flex>
					</Box>
					{hasDescription && (
						<Chevron flexGrow={0} flexShrink={0} rotate={isOpen ? '270deg' : '90deg'}>
							<ChevronRightIcon />
						</Chevron>
					)}
				</Flex>
				{hasDescription && isOpen && (
					<>
						<Separator mx={-3} my={3} />
						<Box py={1}>
							<QuillText value={localize(props.job.jobTitle.description!).text} />
						</Box>
					</>
				)}
			</Card>
		);
	},
	function JobCard() {
		return (
			<Card.Skeleton mb={3}>
				<Flex alignItems="center">
					<Box py={2} flexGrow={1} flexShrink={1}>
						<Heading.Skeleton mb={2} />
						<Paragraph.Skeleton lines={1} />
					</Box>
				</Flex>
			</Card.Skeleton>
		);
	},
);
